import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { filter, shareReplay, take, takeUntil } from 'rxjs/operators';
import {
  MedicalHistoryRiskSummary,
  MedicalHistoryRiskSummaryWithDetail,
  RiskFactor,
  RiskSeverity
} from '../medical-history-risk-factor.schema';
import { DryEyeMedicalHistoryFormGroupValue } from '../dry-eye-medical-history-form.model';
import {
  CategoryKey,
  EMPTY_VIEW_MODEL,
  GROUP_ORDER,
  MedicalHistoryQuickScanViewModelBuilder,
  QuickScanViewModel
} from './medical-history-quick-scan-view-model.builder';

// Slider and numeric fields — show actual value
const SLIDER_FIELDS: Record<string, { suffix: string }> = {
  symptomSeverity:        { suffix: '/ 100' },
  eyeItchiness:           { suffix: '/ 100' },
  eyeTearingQuantity:     { suffix: '/ 100' },
  symptomFrequency:       { suffix: '/ 100' },
  sensitivityToLight:     { suffix: '/ 100' },
  impactOfSymptomsOnLife: { suffix: '/ 100' },
  sensitivityToWind:      { suffix: '/ 100' },
  impactOfSymptomsOnMood: { suffix: '/ 100' },
  stressLevel:            { suffix: '/ 100' },
  cityPollutionLevel:     { suffix: '/ 100' },
  cityHumidityLevel:      { suffix: '/ 100' },
  dietQuality:            { suffix: '/ 100' },
  healthQuality:          { suffix: '/ 100' },
  sleepPerNight:          { suffix: 'hrs/night' },
  caffeinatedDrinksPerDayFreeText: { suffix: 'drinks/day' },
  waterPerDayFreeText:    { suffix: 'glasses/day' },
  workHoursPerDayFreeText: { suffix: 'hrs/day' },
  exercise:               { suffix: 'hrs/week' },
};

// ── Ring geometry ───────────────────────────────────────────────────────────
// Single source of truth for the donut-ring SVG. The radius MUST stay in sync
// with `r="20"` and `cx="24" cy="24"` in the template — change here, change there.
const RING_RADIUS = 20;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;
const RING_START_DEG = -90;
const RING_ROTATION_ORIGIN = '24 24';
const EMPTY_RING_DASH = `0 ${RING_CIRCUMFERENCE.toFixed(1)}`;
const INITIAL_RING_TRANSFORM = `rotate(${RING_START_DEG} ${RING_ROTATION_ORIGIN})`;

// ── Animation timing constants ──────────────────────────────────────────────

// How long the "Analyzing..." spinner phase lasts before the content appears.
// Lower = content appears sooner; higher = more dramatic computing pause.
const GENERATING_DURATION_MS = 1200;

// How long the count-up animation runs AND total cascade duration after spinner.
// Keep aligned with the longest CSS cascade delay (~2.7s) so isRevealComplete — which
// the parent uses to show the header badges — fires after everything settles.
const COUNTER_DURATION_MS = 5000;

@Component({
  selector: 'csi-medical-history-quick-scan',
  templateUrl: './medical-history-quick-scan.component.html',
  styleUrls: ['./medical-history-quick-scan.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MedicalHistoryQuickScanComponent implements OnInit, OnDestroy {
  @Input() medicalHistorySummary$: Observable<MedicalHistoryRiskSummaryWithDetail>;
  @Input() formValue$: Observable<DryEyeMedicalHistoryFormGroupValue>;
  @Input() revealTrigger$: Observable<void>;
  // Optional stream-based restore signal — when fired, jumps straight to the
  // ready content without playing the 1.2s generating spinner. Parents that
  // omit this input fall back to whatever imperative path they were using.
  // CONTRACT: parents MUST pass a ReplaySubject(1) (not a plain Subject) so
  // the signal is buffered for late subscribers — this component may mount
  // after the parent has already fired the trigger (e.g. when a section's
  // *ngIf only resolves to true after an upstream async settle), and a plain
  // Subject would silently lose the signal and leave the view stuck on the
  // empty state.
  @Input() restoreTrigger$: Observable<void>;
  @Input() excludeCategories: CategoryKey[] = [];
  private excludeCategoriesSet: Set<CategoryKey> = new Set();

  // Single source of truth for the rendered view model. The template binds
  // viewModel$ via the async pipe and the reveal/restore animations read the
  // current snapshot via viewModelSubject.value, so we no longer need a
  // separate latestViewModel field whose lifetime had to be hand-managed.
  private viewModelSubject = new BehaviorSubject<QuickScanViewModel>(EMPTY_VIEW_MODEL);
  public viewModel$: Observable<QuickScanViewModel> = this.viewModelSubject.asObservable();

  public notesCache: Record<string, string[]> = {};
  public valueCache: Record<string, string> = {};
  public expandedCategories: Record<string, boolean> = {};
  public isPatientContextExpanded = true;
  public isFitzpatrickExpanded = false;

  public isRevealing = false;
  public isContentReady = false;
  public isRevealComplete = false;
  private revealLoopId: number | null = null;

  public displayTotalCount = 0;

  // Per-severity animated UI state, keyed by severity. One record per concept
  // replaces 9 parallel fields (count/dash/transform × high/moderate/low).
  public displayCount: Record<RiskSeverity, number> = { high: 0, moderate: 0, low: 0 };
  public displayDash: Record<RiskSeverity, string> = { high: EMPTY_RING_DASH, moderate: EMPTY_RING_DASH, low: EMPTY_RING_DASH };
  public displayTransform: Record<RiskSeverity, string> = {
    high: INITIAL_RING_TRANSFORM,
    moderate: INITIAL_RING_TRANSFORM,
    low: INITIAL_RING_TRANSFORM
  };

  // Single schema for everything the template needs to render per-severity:
  // ring stroke color, severity card css, sub-section label/css. Adding or
  // changing a severity is one line — template, ring, cards, and sub-sections
  // all pick it up automatically.
  public readonly severityVisuals: ReadonlyArray<{
    severity: RiskSeverity;
    color: string;
    label: string;        // 'High' / 'Moderate' / 'Low' — sentence case
    shortLabel: string;   // 'HIGH' / 'MOD' / 'LOW' — small-cap chip text
    cardClass: string;    // ring metric card modifier
    subClass: string;     // expanded category sub-section modifier
  }> = [
    { severity: 'high',     color: '#b91c1c', label: 'High',     shortLabel: 'HIGH', cardClass: 'severity-card-high',     subClass: 'severity-label--high' },
    { severity: 'moderate', color: '#ff6b02', label: 'Moderate', shortLabel: 'MOD',  cardClass: 'severity-card-moderate', subClass: 'severity-label--moderate' },
    { severity: 'low',      color: '#ffb938', label: 'Low',      shortLabel: 'LOW',  cardClass: 'severity-card-low',      subClass: 'severity-label--low' }
  ];
  private animationFrameIds: number[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    private changeDetector: ChangeDetectorRef,
    private zone: NgZone,
    private viewModelBuilder: MedicalHistoryQuickScanViewModelBuilder
  ) {}

  ngOnInit(): void {
    // Build the exclusion Set BEFORE wiring subscribers so the first
    // viewModelBuilder.build call already sees the real Set; otherwise excluded
    // categories briefly flash on the first paint and disappear on the second.
    this.excludeCategoriesSet = new Set(this.excludeCategories);

    // One shared combineLatest feeds both the viewModel$ (template) and the
    // rebuildCaches subscription (chip values). Without shareReplay the map
    // inside viewModel$ would fire twice per upstream emission — once for the
    // async pipe in the template and once for the subscribe below.
    const summaryAndFormValue$ = combineLatest([this.medicalHistorySummary$, this.formValue$]).pipe(
      shareReplay(1)
    );

    summaryAndFormValue$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(([summary, formValue]) => {
      try {
        const viewModel = this.viewModelBuilder.build(summary, formValue, this.excludeCategoriesSet);
        this.viewModelSubject.next(viewModel);
      } catch (buildError) {
        console.warn('[MedicalHistoryQuickScan] viewModelBuilder.build failed; rendering empty view model', buildError);
        this.viewModelSubject.next(EMPTY_VIEW_MODEL);
      }
    });

    summaryAndFormValue$.pipe(takeUntil(this.destroy$)).subscribe({
      next: ([summary, formValue]) => {
        try {
          this.rebuildCaches(summary, formValue);
        } catch (rebuildError) {
          console.warn('[MedicalHistoryQuickScan] rebuildCaches failed; chip values may be stale', rebuildError);
        }
      }
    });

    if (this.revealTrigger$) {
      this.revealTrigger$.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.playReveal();
      });
    }

    if (this.restoreTrigger$) {
      this.restoreTrigger$.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.showRestoredContent();
      });
    }

    // Donut total / severity-card counts / ring dash + transform are computed
    // from a viewModel SNAPSHOT taken once when the reveal animation runs. If
    // the patient keeps editing the form after the reveal completes, the parent
    // header badges (bound via async pipe) update live but these snapshot-driven
    // numbers would stay frozen — visible mismatch. Re-apply the final ring
    // state on every subsequent emission once `isRevealComplete` is true.
    this.viewModelSubject.pipe(takeUntil(this.destroy$)).subscribe(viewModel => {
      if (this.isRevealComplete && viewModel !== EMPTY_VIEW_MODEL) {
        this.applyFinalRingState(viewModel);
        this.changeDetector.markForCheck();
      }
    });

    // Default: all categories collapsed so summary preview text is visible
    GROUP_ORDER.forEach(key => { this.expandedCategories[key] = false; });
  }

  ngOnDestroy(): void {
    this.cancelContentAnimations();
    this.cancelRevealLoop();
    this.destroy$.next();
    this.destroy$.complete();
    this.viewModelSubject.complete();
  }

  public playReveal(): void {
    this.cancelContentAnimations();
    this.cancelRevealLoop();
    this.zone.run(() => {
      this.isRevealing = true;
      this.isContentReady = false;
      this.isRevealComplete = false;
      this.displayTotalCount = 0;
      this.severityVisuals.forEach(({ severity }) => {
        this.displayCount[severity] = 0;
        this.displayDash[severity] = EMPTY_RING_DASH;
        this.displayTransform[severity] = INITIAL_RING_TRANSFORM;
      });
      this.changeDetector.markForCheck();
    });

    const startTime = performance.now();
    let phase: 'generating' | 'content' | 'done' = 'generating';

    const tick = () => {
      const elapsed = performance.now() - startTime;

      if (phase === 'generating' && elapsed >= GENERATING_DURATION_MS) {
        phase = 'content';
        this.zone.run(() => {
          this.isRevealing = false;
          this.isContentReady = true;
          this.changeDetector.markForCheck();
          this.startContentAnimation();
        });
      }

      if (phase === 'content' && elapsed >= GENERATING_DURATION_MS + COUNTER_DURATION_MS) {
        phase = 'done';
        this.zone.run(() => {
          this.isRevealComplete = true;
          this.changeDetector.markForCheck();
        });
        this.revealLoopId = null;
        return;
      }

      this.revealLoopId = requestAnimationFrame(tick);
    };

    this.revealLoopId = requestAnimationFrame(tick);
  }

  public showRestoredContent(): void {
    this.cancelContentAnimations();
    this.cancelRevealLoop();
    this.zone.run(() => {
      this.isRevealing = false;
      this.isContentReady = true;
      this.isRevealComplete = true;
      this.changeDetector.markForCheck();
    });

    // Wait for a real (non-error-fallback) emission. If the upstream errored
    // and only EMPTY_VIEW_MODEL is reaching us, restoring would lock the ring
    // at zero permanently — better to let the next real emission paint it.
    this.viewModel$.pipe(
      filter(viewModel => viewModel !== EMPTY_VIEW_MODEL),
      take(1),
      takeUntil(this.destroy$)
    ).subscribe(viewModel => {
      this.applyFinalRingState(viewModel);
      this.changeDetector.markForCheck();
    });
  }

  /**
   * Single source of truth for the donut-ring SVG arithmetic. Returns the
   * per-severity stroke-dasharray and rotation transform corresponding to a
   * count distribution and an easing factor (1 = final state, 0..1 = animating).
   * High always starts at the top (RING_START_DEG); each subsequent severity
   * starts where the previous one ended.
   */
  private computeRingSegments(
    totalCount: number,
    countBySeverity: Record<RiskSeverity, number>,
    eased: number
  ): { dash: Record<RiskSeverity, string>; transform: Record<RiskSeverity, string> } {
    const dash: Record<RiskSeverity, string> = { high: EMPTY_RING_DASH, moderate: EMPTY_RING_DASH, low: EMPTY_RING_DASH };
    const transform: Record<RiskSeverity, string> = {
      high: INITIAL_RING_TRANSFORM,
      moderate: INITIAL_RING_TRANSFORM,
      low: INITIAL_RING_TRANSFORM
    };
    if (totalCount <= 0) {
      return { dash, transform };
    }
    let cumulativeArc = 0;
    this.severityVisuals.forEach(({ severity }, index) => {
      const arc = (countBySeverity[severity] / totalCount) * RING_CIRCUMFERENCE * eased;
      dash[severity] = arc.toFixed(1) + ' ' + (RING_CIRCUMFERENCE - arc).toFixed(1);
      if (index > 0) {
        const startDeg = RING_START_DEG + (cumulativeArc / RING_CIRCUMFERENCE) * 360;
        transform[severity] = 'rotate(' + startDeg.toFixed(1) + ' ' + RING_ROTATION_ORIGIN + ')';
      }
      cumulativeArc += arc;
    });
    return { dash, transform };
  }

  private applyFinalRingState(viewModel: QuickScanViewModel): void {
    const totalCount = viewModel.totalCount;
    const vmCountBySeverity: Record<RiskSeverity, number> = {
      high:     viewModel.highCount,
      moderate: viewModel.moderateCount,
      low:      viewModel.lowCount
    };
    this.displayTotalCount = totalCount;
    this.severityVisuals.forEach(({ severity }) => {
      this.displayCount[severity] = vmCountBySeverity[severity];
    });
    const segments = this.computeRingSegments(totalCount, vmCountBySeverity, 1);
    this.severityVisuals.forEach(({ severity }) => {
      this.displayDash[severity] = segments.dash[severity];
      this.displayTransform[severity] = segments.transform[severity];
    });
  }

  private cancelRevealLoop(): void {
    if (this.revealLoopId !== null) {
      cancelAnimationFrame(this.revealLoopId);
      this.revealLoopId = null;
    }
  }

  private cancelContentAnimations(): void {
    this.animationFrameIds.forEach((frameId: number) => cancelAnimationFrame(frameId));
    this.animationFrameIds = [];
  }

  private startContentAnimation(): void {
    const viewModel = this.viewModelSubject.value;
    if (viewModel === EMPTY_VIEW_MODEL) {
      console.warn('[MedicalHistoryQuickScan] startContentAnimation called before viewModel$ emitted; ring will stay at zero');
      return;
    }
    const totalCount = viewModel.totalCount;
    const startTime = performance.now();

    const step = () => {
      const elapsed = performance.now() - startTime;
      const progress = Math.min(elapsed / COUNTER_DURATION_MS, 1);
      const eased = 1 - Math.pow(1 - progress, 3);

      this.displayTotalCount = Math.round(viewModel.totalCount * eased);

      const vmCountBySeverity: Record<RiskSeverity, number> = {
        high:     viewModel.highCount,
        moderate: viewModel.moderateCount,
        low:      viewModel.lowCount
      };
      const segments = this.computeRingSegments(totalCount, vmCountBySeverity, eased);
      this.severityVisuals.forEach(({ severity }) => {
        this.displayCount[severity] = Math.round(vmCountBySeverity[severity] * eased);
        this.displayDash[severity] = segments.dash[severity];
        this.displayTransform[severity] = segments.transform[severity];
      });

      this.changeDetector.markForCheck();

      if (progress < 1) {
        const frameId = requestAnimationFrame(step);
        this.animationFrameIds.push(frameId);
      }
    };

    const frameId = requestAnimationFrame(step);
    this.animationFrameIds.push(frameId);
  }

  toggleCategory(key: string): void {
    this.expandedCategories[key] = !this.expandedCategories[key];
  }

  togglePatientContext(): void {
    this.isPatientContextExpanded = !this.isPatientContextExpanded;
  }

  toggleFitzpatrick(): void {
    this.isFitzpatrickExpanded = !this.isFitzpatrickExpanded;
  }

  private rebuildCaches(summary: MedicalHistoryRiskSummary, formValue: DryEyeMedicalHistoryFormGroupValue): void {
    const notes: Record<string, string[]> = {};
    const values: Record<string, string> = {};

    // Free-text notes — derived from emitted risk factors' detail
    const allFactors: RiskFactor[] = [
      ...(summary ? summary.high : []),
      ...(summary ? summary.moderate : []),
      ...(summary ? summary.low : [])
    ];
    allFactors.forEach(medicalHistoryRiskFactor => {
      if (!medicalHistoryRiskFactor.detail) { return; }
      const lines: string[] = [];
      medicalHistoryRiskFactor.detail.split('\n').forEach(detailLine => {
        const trimmed = detailLine.trim();
        if (!trimmed) { return; }
        // Normalize patient first-person prefixes that come from English locale
        // detail strings (e.g. "I have severe eye pain" → "Severe eye pain") so
        // the chip reads as a clinical note rather than a quote. This regex is
        // English-only; non-English locales come through with the original
        // wording untouched, which is the desired behavior because the rest of
        // the chip text is already translated upstream by the schema service.
        const stripped = trimmed.replace(/^I\s+ha(?:ve|d)\s+/i, '').replace(/^I\s+need\s+/i, '').trim();
        const line = stripped ? stripped.charAt(0).toUpperCase() + stripped.slice(1) : trimmed;
        lines.push(line);
      });
      if (lines.length > 0) {
        notes[medicalHistoryRiskFactor.key] = lines;
      }
    });

    if (!formValue) {
      this.notesCache = notes;
      this.valueCache = values;
      return;
    }

    // Slider / numeric values
    Object.keys(SLIDER_FIELDS).forEach(factorKey => {
      const raw = formValue[factorKey];
      if (raw === null || raw === undefined || raw === '') { return; }
      const num = Number(raw);
      if (isNaN(num)) { return; }
      const { suffix } = SLIDER_FIELDS[factorKey];
      values[factorKey] = `${num} ${suffix}`;
    });

    // Duration object values — { month, year, total }
    const duration = formValue['symptomDuration'];
    if (duration && typeof duration === 'object' && duration.total != null) {
      const totalMonths = Number(duration.total);
      if (!isNaN(totalMonths) && totalMonths > 0) {
        const years = Math.floor(totalMonths / 12);
        const months = totalMonths % 12;
        const parts: string[] = [];
        if (years > 0) { parts.push(`${years}y`); }
        if (months > 0) { parts.push(`${months}mo`); }
        if (parts.length === 0) { parts.push(`${totalMonths}mo`); }
        values['symptomDuration'] = parts.join(' ');
      }
    }

    this.notesCache = notes;
    this.valueCache = values;
  }
}
